import { ref as storageRef, uploadBytes } from "firebase/storage";
import { storage } from "./firebase-config.js";
import { track } from "./analytics.js";
import { hexToRgb } from "./utils/colorUtils.js";
import {
  PLAN_PRICING,
  isSelfServePlan,
  MAX_SELF_SERVE_LOCATIONS,
} from "./utils/pricingPlans.js";
import {
  CLOUD_FUNCTIONS_BASE_URL,
  CLOUD_FUNCTIONS_AUTH_TOKEN,
} from "./firebase-functions.js";

// Stripe prices for the self-serve plans. The amounts live in Stripe, so
// these must point at prices matching src/utils/pricingPlans.js. Established
// is contact-only and has no self-serve checkout.
const PRICE_IDS = {
  starter: {
    monthly: "price_1UNZafGRg3FFnovAg2WuwF2g",
    annual:  "price_1UNZdiGRg3FFnovAu6S957F3",
  },
  scaler: {
    monthly: "price_1UNZerGRg3FFnovAuRLi0Hei",
    annual:  "price_1UNZfcGRg3FFnovApBPVxdYX",
  },
};

const callBackend = async (endpoint, body) => {
  const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: CLOUD_FUNCTIONS_AUTH_TOKEN,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `${endpoint} failed: ${response.status}`);
  }
  return response.json();
};

const resolvePriceId = (formData) => {
  const planPrices = isSelfServePlan(formData.packagePlan)
    ? PRICE_IDS[formData.packagePlan]
    : null;
  if (!planPrices) throw new Error("Invalid plan selected");
  const priceId = planPrices[formData.billingPeriod];
  if (!priceId) throw new Error("Invalid billing period selected");
  return priceId;
};

// How many locations the business trades from. Stored on the business and,
// for per-location plans, used as the Stripe quantity. The plan itself is
// never derived from it. Anything above the self-serve cap goes through the
// Established enquiry and must never reach checkout.
const resolveLocations = (formData) => {
  const locations = parseInt(formData.numberOfLocations, 10);
  if (
    !Number.isInteger(locations) ||
    locations < 1 ||
    locations > MAX_SELF_SERVE_LOCATIONS
  ) {
    throw new Error("Invalid number of locations");
  }
  return locations;
};

// Uploads the logo to Firebase Storage. Requires the caller to be signed in.
const uploadLogoStep = async (formData, logoFile) => {
  const logoRef = storageRef(storage, `${formData.id}.png`);
  try {
    await uploadBytes(logoRef, logoFile);
  } catch (e) {
    track("businessOnboarding_step_failed", {
      business_id: formData.id,
      step: "logo_upload",
      error_message: e.message,
    });
    throw e;
  }
  track("businessOnboarding_logo_uploaded", { business_id: formData.id });
};

// Creates the Stripe Checkout session via the Cloud Function and returns the
// URL the browser should redirect to. The Cloud Function is responsible for
// writing the `businesses/<id>` node and the `employees/<email>` index when
// the webhook fires `checkout.session.completed`.
const createCheckoutStep = async (formData, priceId) => {
  const bgRgb = hexToRgb(formData.backgroundColor);
  const fgRgb = hexToRgb(formData.foregroundColor);
  const locations = resolveLocations(formData);
  // Per-location plans are billed once for every location.
  const quantity = PLAN_PRICING[formData.packagePlan].perLocation ? locations : 1;

  const businessData = {
    adminEmail:            formData.adminEmail,
    adminName:             formData.adminName,
    // Neither sign-up nor Settings collects this any more, but the backend
    // writes it straight to RTDB and Firebase rejects `undefined`.
    businessType:          "",
    displayName:           formData.displayName,
    numberOfLocations:     locations,
    isActive:              false,
    packagePlan:           formData.packagePlan,
    billingPeriod:         formData.billingPeriod,
    createdAt:             new Date().toISOString(),
    thresholdForRedeeming: parseInt(formData.thresholdForRedeeming),
    rewardsCardData: {
      id:              formData.id,
      type:            "eventTicket",
      displayName:     formData.displayName,
      logoImageName:   formData.logoImageName,
      backgroundColor: bgRgb,
      foregroundColor: fgRgb,
      labelColor:      fgRgb,
      passVersion:     "1",
      auxiliaryFieldLabel: formData.auxiliaryFieldLabel || " ",
      auxiliaryFieldValue: formData.auxiliaryFieldValue || " ",
    },
    webDisplayData: {
      title:           formData.title,
      subtitle:        formData.subtitle,
      backgroundColor: bgRgb,
      textColor:       fgRgb,
    },
    // QR code counter sign design. Colours are inherited from the branding step
    // (the backend reads them from `rewardsCardData`); `showLogo` reuses the
    // same logo uploaded for the rewards card.
    qrCodeDesign: {
      headline: (formData.qrHeadline || "Join our rewards programme!").trim(),
      showLogo: formData.qrShowLogo !== false,
    },
  };

  let checkoutUrl;
  try {
    const result = await callBackend("createStripeCheckoutSession", {
      businessId:  formData.id,
      email:       formData.adminEmail,
      priceId,
      quantity,
      successUrl:  `${window.location.origin}/signup-success`,
      cancelUrl:   `${window.location.origin}/signup-cancelled`,
      businessData,
    });
    checkoutUrl = result.url;
  } catch (e) {
    track("businessOnboarding_step_failed", {
      business_id: formData.id,
      step: "stripe_checkout",
      error_message: e.message,
    });
    throw e;
  }
  track("businessOnboarding_checkout_created", {
    business_id: formData.id,
    plan: formData.packagePlan,
    billing_period: formData.billingPeriod,
  });
  return checkoutUrl;
};

// Provisions the Android Wallet loyalty class. iOS parity.
const updateAndroidPassClassStep = async (formData) => {
  try {
    await callBackend("updateAndroidPassClass", {
      classId:            `3388000000022951634.client_${formData.id}_v1`,
      businessName:       formData.displayName,
      hexBackgroundColor: formData.backgroundColor,
      logoImageName:      formData.logoImageName,
      stampData: {
        businessId:      formData.id,
        count:           0,
        redeemThreshold: parseInt(formData.thresholdForRedeeming),
        foregroundColor: formData.foregroundColor,
        backgroundColor: formData.backgroundColor,
      },
    });
  } catch (e) {
    track("businessOnboarding_step_failed", {
      business_id: formData.id,
      step: "wallet_class",
      error_message: e.message,
    });
    throw e;
  }
  track("businessOnboarding_wallet_class_created", { business_id: formData.id });
};

// M3 onboarding flow. The owner already has a Firebase Auth account (created
// during the M2 sign-up step) and is signed in, so we skip the legacy
// `registerEmployee` + sign-in steps and go straight to the logo upload +
// Stripe checkout + Android wallet class creation.
//
// `formData.adminEmail` must be supplied by the caller (sourced from
// `authStore.user.email`).
export const saveBusinessRegistration = async (formData, logoFile) => {
  // Both throw before anything is uploaded, so a bad plan or count never
  // leaves a stray logo in Storage.
  const priceId = resolvePriceId(formData);
  resolveLocations(formData);
  track("businessOnboarding_started", {});

  await uploadLogoStep(formData, logoFile);
  const checkoutUrl = await createCheckoutStep(formData, priceId);
  await updateAndroidPassClassStep(formData);

  track("businessOnboarding_checkout_redirected", {
    business_id: formData.id,
    plan: formData.packagePlan,
  });
  return checkoutUrl;
};
